import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Calculator, Download, TrendingUp, Clock, Users, DollarSign, Percent, Sparkles } from "lucide-react";
import jsPDF from "jspdf";
import SiteLayout from "@/components/site/SiteLayout";
import Seo from "@/components/site/Seo";
import { SectionTag, Reveal } from "@/components/site/Reveal";
import ParticleField from "@/components/site/ParticleField";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";

const LABELS = {
  ar: {
    seo_title: "حاسبة عائد الاستثمار (ROI)",
    seo_desc: "احسب كم ستوفّر شركتك عند اعتماد نظام ERP/CRM من مدينتي.",
    tag: "أداة تقدير الوفورات",
    title: "احسب عائد استثمارك",
    subtitle: "أدخل بيانات شركتك واحصل على تقدير سنوي للوفورات المتوقّعة عند الاعتماد على أنظمة مدينتي، مع تقرير PDF قابل للتحميل.",
    company_name: "اسم الشركة",
    company_ph: "شركتك المحدودة",
    employees: "عدد الموظفين المتأثرين",
    hourly_cost: "متوسط تكلفة الساعة (USD)",
    hours_wasted: "ساعات مهدورة يوميًا لكل موظف (على المهام اليدوية)",
    working_days: "أيام العمل شهريًا",
    efficiency: "نسبة التحسّن المتوقّعة من الأتمتة",
    system_cost: "التكلفة السنوية للنظام (USD)",
    results: "النتائج",
    annual_savings: "الوفورات السنوية المتوقّعة",
    annual_hours: "ساعات موفّرة سنويًا",
    payback: "استرداد الاستثمار خلال",
    roi_percent: "عائد الاستثمار السنوي",
    months: "شهر",
    hours: "ساعة",
    download: "تحميل التقرير PDF",
    disclaimer: "* التقديرات إرشادية وتعتمد على المدخلات. تواصل معنا للحصول على دراسة مخصصة.",
    net_savings: "صافي الوفورات (بعد خصم تكلفة النظام)",
    pdf_title: "تقرير حاسبة عائد الاستثمار — مدينتي",
    pdf_prepared: "تم الإعداد بواسطة Madinaty IT Solutions",
    pdf_date: "التاريخ",
    pdf_footer: "www.madenati.dlr.iq  |  للحصول على دراسة تفصيلية تواصل معنا",
  },
  en: {
    seo_title: "ROI Calculator",
    seo_desc: "Estimate how much your business will save by adopting Madinaty ERP/CRM.",
    tag: "Savings Estimator",
    title: "Calculate your ROI",
    subtitle: "Enter your company details and get an annual estimate of savings when adopting Madinaty systems, with a downloadable PDF report.",
    company_name: "Company name",
    company_ph: "Your Company LLC",
    employees: "Affected employees",
    hourly_cost: "Average hourly cost (USD)",
    hours_wasted: "Daily hours wasted per employee on manual tasks",
    working_days: "Working days per month",
    efficiency: "Expected automation efficiency gain",
    system_cost: "Annual system cost (USD)",
    results: "Results",
    annual_savings: "Estimated annual savings",
    annual_hours: "Hours saved per year",
    payback: "Payback period",
    roi_percent: "Annual ROI",
    months: "months",
    hours: "hours",
    download: "Download PDF Report",
    disclaimer: "* Estimates are indicative and depend on inputs. Contact us for a tailored study.",
    net_savings: "Net savings (after system cost)",
    pdf_title: "ROI Calculator Report — Madinaty",
    pdf_prepared: "Prepared by Madinaty IT Solutions",
    pdf_date: "Date",
    pdf_footer: "www.madenati.dlr.iq  |  Contact us for a detailed study",
  },
  ku: {
    seo_title: "ژمێرەری قازانجی وەبەرهێنان (ROI)",
    seo_desc: "بزانە شیرکەتەکەت چەند دەپارێزێت لە کاتێکدا سیستەمی ERP/CRM مەدینەتی بەکاردەهێنی.",
    tag: "ژماردنی پاشەکەوت",
    title: "ROI شیرکەتەکەت بژمێرە",
    subtitle: "زانیاری شیرکەتەکەت بنووسە و ژمارەیەکی ساڵانە لە پاشەکەوتەکان وەربگرە بە دابەزینی سیستەمەکانی مەدینەتی، لەگەڵ ڕاپۆرتی PDF بۆ داگرتن.",
    company_name: "ناوی شیرکەت",
    company_ph: "شیرکەتەکەت",
    employees: "کارمەندی کاریگەر",
    hourly_cost: "بەرامبەری کاتژمێر (USD)",
    hours_wasted: "کاتژمێری بەفیڕۆچوو ڕۆژانە بۆ هەر کارمەندێک",
    working_days: "ڕۆژانی کار مانگانە",
    efficiency: "بەراوردی چاکسازی چاوەڕوانکراو",
    system_cost: "تێچووی ساڵانەی سیستەم (USD)",
    results: "ئەنجامەکان",
    annual_savings: "پاشەکەوتی ساڵانەی چاوەڕوانکراو",
    annual_hours: "کاتژمێری پاشەکەوتکراو ساڵانە",
    payback: "گەڕانەوەی وەبەرهێنان لە ماوەی",
    roi_percent: "ROI ی ساڵانە",
    months: "مانگ",
    hours: "کاتژمێر",
    download: "داگرتنی ڕاپۆرتی PDF",
    disclaimer: "* ژمێرەکان ڕاسپاردەن و پشت بە داتاکان دەبەستن.",
    net_savings: "پاشەکەوتی پاک",
    pdf_title: "ڕاپۆرتی ژمێرەری ROI — مەدینەتی",
    pdf_prepared: "ئامادەکراو لەلایەن مەدینەتی",
    pdf_date: "بەروار",
    pdf_footer: "www.madenati.dlr.iq  |  پەیوەندیمان پێوە بکە",
  },
};

const fmtUSD = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);
const fmtNum = (n) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Math.max(0, n || 0));

function Slider({ label, icon: Icon, value, onChange, min, max, step, unit, testId }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="flex items-center gap-2 text-sm text-slate-300">
          <Icon size={16} className="text-primary shrink-0" /> {label}
        </label>
        <div className="text-sm font-extrabold text-primary tabular-nums">{value}{unit}</div>
      </div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} className="w-full accent-[color:var(--primary)]" data-testid={testId} />
      <div className="flex justify-between text-[10px] text-slate-500 mt-1"><span>{min}{unit}</span><span>{max}{unit}</span></div>
    </div>
  );
}

export default function ROICalculator() {
  const { lang } = useLang();
  const L = LABELS[lang] || LABELS.ar;

  const [form, setForm] = useState({
    company: "",
    employees: 20,
    hourly_cost: 8,
    hours_wasted: 2,
    working_days: 22,
    efficiency: 60,
    system_cost: 5000,
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const results = useMemo(() => {
    const monthlyHoursLost = form.employees * form.hours_wasted * form.working_days;
    const annualHoursLost = monthlyHoursLost * 12;
    const annualHoursSaved = annualHoursLost * (form.efficiency / 100);
    const grossAnnualSavings = annualHoursSaved * form.hourly_cost;
    const netAnnualSavings = grossAnnualSavings - form.system_cost;
    const roi = form.system_cost > 0 ? (netAnnualSavings / form.system_cost) * 100 : 0;
    const paybackMonths = grossAnnualSavings > 0 ? (form.system_cost / (grossAnnualSavings / 12)) : 0;
    return { annualHoursSaved, grossAnnualSavings, netAnnualSavings, roi, paybackMonths };
  }, [form]);

  const downloadPDF = () => {
    try {
      const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
      const marginX = 48;
      let y = 64;

      // Header bar
      doc.setFillColor(0, 242, 96);
      doc.rect(0, 0, 595.28, 6, "F");

      // Brand
      doc.setFont("helvetica", "bold");
      doc.setTextColor(20, 30, 50);
      doc.setFontSize(20);
      doc.text("Madinaty IT Solutions", marginX, y);
      y += 26;
      doc.setFontSize(14);
      doc.setTextColor(100, 100, 100);
      doc.text("ROI Calculator Report", marginX, y);
      y += 20;

      doc.setFontSize(10);
      doc.setTextColor(150, 150, 150);
      const nowStr = new Date().toLocaleDateString(lang === "ar" ? "en-GB" : "en-US");
      doc.text(`${L.pdf_date}: ${nowStr}`, marginX, y);
      y += 22;

      // Company block
      doc.setDrawColor(230, 230, 230);
      doc.line(marginX, y, 595.28 - marginX, y);
      y += 22;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(20, 30, 50);
      doc.text(`Prepared for: ${form.company || "—"}`, marginX, y);
      y += 24;

      // Inputs table
      const inputs = [
        ["Employees", `${form.employees}`],
        ["Avg hourly cost", `$${form.hourly_cost}`],
        ["Hours wasted / day / employee", `${form.hours_wasted} hrs`],
        ["Working days / month", `${form.working_days}`],
        ["Efficiency gain", `${form.efficiency}%`],
        ["Annual system cost", fmtUSD(form.system_cost)],
      ];
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(20, 30, 50);
      doc.text("Inputs", marginX, y);
      y += 14;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(60, 60, 60);
      inputs.forEach(([k, v]) => {
        doc.text(k, marginX, y);
        doc.text(v, 595.28 - marginX, y, { align: "right" });
        y += 16;
      });

      y += 12;
      doc.setDrawColor(230, 230, 230);
      doc.line(marginX, y, 595.28 - marginX, y);
      y += 24;

      // Results
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.setTextColor(20, 30, 50);
      doc.text("Results", marginX, y);
      y += 22;

      const rows = [
        ["Hours saved / year", `${fmtNum(results.annualHoursSaved)} hrs`],
        ["Gross annual savings", fmtUSD(results.grossAnnualSavings)],
        ["Net annual savings", fmtUSD(results.netAnnualSavings)],
        ["Payback period", `${fmtNum(results.paybackMonths)} months`],
        ["Annual ROI", `${fmtNum(results.roi)}%`],
      ];

      rows.forEach(([k, v]) => {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(11);
        doc.setTextColor(80, 80, 80);
        doc.text(k, marginX, y);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 150, 60);
        doc.text(v, 595.28 - marginX, y, { align: "right" });
        y += 18;
      });

      // Big highlight box
      y += 20;
      doc.setFillColor(240, 253, 244);
      doc.roundedRect(marginX, y, 595.28 - marginX * 2, 74, 10, 10, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(0, 100, 40);
      doc.text("Estimated annual savings", marginX + 20, y + 24);
      doc.setFontSize(28);
      doc.setTextColor(0, 150, 60);
      doc.text(fmtUSD(results.grossAnnualSavings), marginX + 20, y + 56);

      y += 100;
      doc.setFontSize(9);
      doc.setTextColor(120, 120, 120);
      doc.text(L.disclaimer, marginX, y, { maxWidth: 595.28 - marginX * 2 });

      // Footer
      doc.setFontSize(9);
      doc.setTextColor(140, 140, 140);
      doc.text(L.pdf_footer, marginX, 820);

      const safeName = (form.company || "madinaty").replace(/[^\w-]+/g, "_");
      doc.save(`ROI_${safeName}.pdf`);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("PDF failed");
    }
  };

  const inputCls = "w-full rounded-xl bg-[#0b1731] border border-white/10 focus:border-[color:var(--primary)] px-4 py-2.5 text-sm outline-none";

  return (
    <SiteLayout>
      <Seo title={L.seo_title} description={L.seo_desc} />

      <section className="relative overflow-hidden pt-16 pb-10" data-testid="roi-page">
        <ParticleField className="opacity-40" density={30} />
        <div className="absolute inset-0 radial-fade" />
        <div className="relative z-10 mx-auto max-w-5xl px-5 sm:px-8">
          <SectionTag>{L.tag}</SectionTag>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mt-5 mb-4">{L.title}</h1>
          <p className="text-slate-400 text-lg max-w-2xl">{L.subtitle}</p>
        </div>
      </section>

      <section className="section-pad pt-4">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 grid lg:grid-cols-2 gap-6">
          {/* Left: form */}
          <Reveal>
            <div className="card-surface rounded-3xl p-6 sm:p-8 space-y-6" data-testid="roi-form">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-2xl grid place-items-center border border-[color:var(--primary)]/30 bg-[color:var(--primary)]/10">
                  <Calculator size={22} className="text-primary" />
                </div>
                <div>
                  <div className="font-extrabold text-lg">Inputs</div>
                  <div className="text-xs text-slate-500">أدخل بيانات شركتك</div>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1.5">{L.company_name}</label>
                <input value={form.company} onChange={(e) => set("company", e.target.value)} placeholder={L.company_ph} className={inputCls} data-testid="roi-company" />
              </div>
              <Slider label={L.employees} icon={Users} value={form.employees} onChange={(v) => set("employees", v)} min={1} max={500} step={1} unit="" testId="roi-slider-employees" />
              <Slider label={L.hourly_cost} icon={DollarSign} value={form.hourly_cost} onChange={(v) => set("hourly_cost", v)} min={2} max={50} step={1} unit=" $" testId="roi-slider-hourly_cost" />
              <Slider label={L.hours_wasted} icon={Clock} value={form.hours_wasted} onChange={(v) => set("hours_wasted", v)} min={0.5} max={6} step={0.5} unit=" h" testId="roi-slider-hours_wasted" />
              <Slider label={L.working_days} icon={Calculator} value={form.working_days} onChange={(v) => set("working_days", v)} min={15} max={26} step={1} unit=" d" testId="roi-slider-working_days" />
              <Slider label={L.efficiency} icon={Percent} value={form.efficiency} onChange={(v) => set("efficiency", v)} min={20} max={90} step={5} unit=" %" testId="roi-slider-efficiency" />
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1.5">{L.system_cost}</label>
                <input type="number" min={0} value={form.system_cost} onChange={(e) => set("system_cost", Number(e.target.value))} className={inputCls} dir="ltr" data-testid="roi-system-cost" />
              </div>
            </div>
          </Reveal>

          {/* Right: results */}
          <Reveal delay={0.1}>
            <div className="space-y-5 sticky top-24" data-testid="roi-results">
              <motion.div
                className="rounded-3xl p-8 text-center relative overflow-hidden"
                style={{ background: "linear-gradient(135deg, rgba(0,242,96,0.15), rgba(0,242,96,0.02))", border: "1px solid rgba(0,242,96,0.35)" }}
                key={results.grossAnnualSavings}
                initial={{ scale: 0.98, opacity: 0.7 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.35 }}
              >
                <div className="absolute -top-10 -end-10 opacity-20"><Sparkles size={140} className="text-primary" /></div>
                <div className="text-xs uppercase tracking-wider text-primary font-bold mb-3">{L.annual_savings}</div>
                <div className="text-5xl sm:text-6xl font-black text-primary tabular-nums" style={{ textShadow: "0 0 40px rgba(0,242,96,0.5)" }} data-testid="roi-savings">
                  {fmtUSD(results.grossAnnualSavings)}
                </div>
              </motion.div>

              <div className="grid grid-cols-2 gap-4">
                <div className="card-surface rounded-2xl p-5 text-center">
                  <TrendingUp size={20} className="text-primary mx-auto mb-2" />
                  <div className="text-xs text-slate-400 mb-1">{L.annual_hours}</div>
                  <div className="text-2xl font-extrabold tabular-nums" data-testid="roi-hours">{fmtNum(results.annualHoursSaved)}</div>
                </div>
                <div className="card-surface rounded-2xl p-5 text-center">
                  <Clock size={20} className="text-primary mx-auto mb-2" />
                  <div className="text-xs text-slate-400 mb-1">{L.payback}</div>
                  <div className="text-2xl font-extrabold tabular-nums" data-testid="roi-payback">{fmtNum(results.paybackMonths)} {L.months}</div>
                </div>
                <div className="card-surface rounded-2xl p-5 text-center">
                  <Percent size={20} className="text-primary mx-auto mb-2" />
                  <div className="text-xs text-slate-400 mb-1">{L.roi_percent}</div>
                  <div className="text-2xl font-extrabold tabular-nums" data-testid="roi-percent">{fmtNum(results.roi)}%</div>
                </div>
                <div className="card-surface rounded-2xl p-5 text-center">
                  <DollarSign size={20} className="text-primary mx-auto mb-2" />
                  <div className="text-xs text-slate-400 mb-1">{L.net_savings}</div>
                  <div className="text-2xl font-extrabold tabular-nums" data-testid="roi-net">{fmtUSD(results.netAnnualSavings)}</div>
                </div>
              </div>

              <button
                onClick={downloadPDF}
                className="w-full rounded-full py-4 font-extrabold text-[#04120a] flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform"
                style={{ background: "var(--primary)", boxShadow: "0 10px 40px rgba(0,242,96,0.35)" }}
                data-testid="roi-download-pdf"
              >
                <Download size={18} /> {L.download}
              </button>
              <p className="text-xs text-slate-500 text-center">{L.disclaimer}</p>
            </div>
          </Reveal>
        </div>
      </section>
    </SiteLayout>
  );
}
